import { apiFetch, apiFetchResponse } from './client';
import { isApiError } from './errors';

/**
 * Turn a client failure into the message the four document screens render.
 *
 * They all show `err.message` verbatim, so the wording has to stay stable and
 * has to name the STATUS — "Could not generate the PDF (500)" is a support
 * ticket someone can act on; the server's raw internal message is not.
 *
 * The session-ending side effects have already run inside `apiFetchResponse` by
 * the time this is reached, so flattening the error here costs nothing: a
 * revoked session has already flipped the app to anonymous and the redirect to
 * /login wins the race against this string ever being displayed.
 */
function asDisplayError(error: unknown, verb: string): Error {
  if (isApiError(error)) {
    return new Error(`${verb} (${error.status})`);
  }
  // A network-level failure (offline, DNS, aborted) — no status to quote.
  return error instanceof Error ? error : new Error(verb);
}

/**
 * Fetch bytes from an AUTHENTICATED endpoint, as a Blob.
 *
 * THIS IS THE WHOLE REASON THIS MODULE EXISTS, and the one rule every file
 * route in the app has to obey: the API authenticates with a bearer token in
 * an `Authorization` header, and a plain `<a href="/api/...">`, an
 * `<img src="...">` or a `window.open(url)` sends NO SUCH HEADER. Every one of
 * them 401s — silently, as a broken image or a downloaded error page — no
 * matter that the user is signed in and the screen around it is working.
 * `InvoiceSettingsController`'s own doc comment documents the identical trap
 * for the payment-QR image, which is inlined as a data URL precisely to dodge
 * it.
 *
 * So bytes are fetched in JS, where the header can be attached, and only then
 * turned into a blob: URL — which needs no credential at all, because it
 * addresses memory this page already holds.
 *
 * Through `apiFetchResponse`, never a hand-rolled `fetch`: that buys the
 * refresh-once/replay-once behaviour and the SINGLE-FLIGHT refresh (two
 * downloads racing on an expired token share one `/auth/refresh` instead of
 * presenting a rotated token twice and getting the family revoked as suspected
 * theft). `apiFetchResponse` rather than `apiFetch` because the body is a file,
 * and `apiFetch` parses every success as JSON.
 */
async function fetchAuthenticatedBlob(path: string, accept: string, verb: string): Promise<Blob> {
  const response = await apiFetchResponse(path, { headers: { Accept: accept } }).catch(
    (error: unknown) => {
      throw asDisplayError(error, verb);
    },
  );
  return response.blob();
}

/**
 * Hand a blob to the browser as a saved file.
 *
 * The object URL is revoked straight after the click so the bytes are not
 * pinned in memory for the life of the tab.
 */
function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * Download a server-generated PDF.
 *
 * NOT `window.print()`. The browser print dialog photographs the whole SCREEN,
 * which is why the credit-note PDF arrived with the app's page header — the
 * "Credit notes / Download PDF / + New credit note" bar — printed above the
 * document. This fetches the real file from the PDF endpoint, where headless
 * Chrome renders the document on its own.
 *
 * The credential goes in the Authorization header rather than the URL, so it
 * never lands in browser history, and the object URL is revoked straight after
 * the click so the blob is not pinned in memory.
 */
/**
 * The document families that have a `/pdf` route.
 *
 * `payments/receipts` is a path segment pair rather than a single word because
 * the receipt endpoint lives UNDER payments — `GET /payments/receipts/:id/pdf`
 * mirrors the `GET /payments/receipts/:id` the ledger already reads, so the
 * download carries the same `PAYMENTS_RECEIPTS` permission as the screen.
 */
export type PdfKind = 'invoices' | 'vouchers' | 'credit-notes' | 'payments/receipts';

/**
 * API family -> the `/print/:type/:id` segment the print page routes on.
 *
 * A lookup rather than a nested ternary because it must stay exhaustive: the
 * server's `PrintableDocument` union and this map are the two halves of one
 * contract, and a missing entry is a compile error rather than a silent
 * `undefined` in the iframe URL.
 */
const PRINT_TYPE: Readonly<Record<PdfKind, 'invoice' | 'voucher' | 'credit-note' | 'receipt'>> =
  Object.freeze({
    invoices: 'invoice',
    vouchers: 'voucher',
    'credit-notes': 'credit-note',
    'payments/receipts': 'receipt',
  });

export async function downloadDocumentPdf(
  kind: PdfKind,
  id: string,
  filename: string,
): Promise<void> {
  /**
   * This module used to call `fetch` directly with
   * `Bearer ${getAccessToken() ?? ''}`. The access token lives in memory only,
   * so after a reload or its 15-minute expiry it was null — and `?? ''` sent the
   * literal header `Bearer `, which the server reads as no credential at all.
   * Every other screen silently refreshed and carried on; every PDF download
   * 401'd permanently with no recovery, for all four document types. See
   * `fetchAuthenticatedBlob` for what going back through the shared client buys.
   */
  const blob = await fetchAuthenticatedBlob(
    `/${kind}/${encodeURIComponent(id)}/pdf`,
    'application/pdf',
    'Could not generate the PDF',
  );
  saveBlob(blob, filename);
}

/* ── monthly report: a server-generated .xlsx, not a PDF ────────────────────── */

/**
 * `GET /reports/monthly-workbook?month=YYYY-MM` — same authenticated-blob
 * mechanism as `downloadDocumentPdf` above, and the same non-negotiable
 * reason: a bare `<a href>` sends no `Authorization` header and 401s.
 */
export async function downloadMonthlyReport(month: string, filename: string): Promise<void> {
  const blob = await fetchAuthenticatedBlob(
    `/reports/monthly-workbook?month=${encodeURIComponent(month)}`,
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'Could not generate the report',
  );
  saveBlob(blob, filename);
}

/**
 * `GET /reports/monthly-overview/export` — the "Export this table to Excel"
 * button on the Reports page's monthly overview (Part C), a single-sheet
 * export distinct from the 13-18 sheet monthly workbook above.
 */
export async function downloadMonthlyOverview(filename: string): Promise<void> {
  const blob = await fetchAuthenticatedBlob(
    '/reports/monthly-overview/export',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'Could not generate the export',
  );
  saveBlob(blob, filename);
}

/* ── expenses export: a real .xlsx, separate from the monthly report ──────── */

/**
 * `GET /expenses/export?from=&to=` — the Expenses page's own "Export"
 * button, same authenticated-blob mechanism as the monthly report above.
 */
export async function downloadExpensesExport(from: string, to: string, filename: string): Promise<void> {
  const blob = await fetchAuthenticatedBlob(
    `/expenses/export?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'Could not generate the export',
  );
  saveBlob(blob, filename);
}

/* ── tour brochure: an UPLOADED file, not a rendered document ──────────────── */

/**
 * `GET /tours/:id/brochure` — the one route that serves bytes somebody uploaded
 * rather than bytes the server drew.
 *
 * Same authenticated-fetch mechanism as the document PDFs above, and for the
 * same non-negotiable reason (see `fetchAuthenticatedBlob`). It is worth saying
 * once more here because a brochure LOOKS like the kind of thing that wants a
 * plain link — it is a static file with a stable URL — and it is not: the route
 * carries `TOUR_MASTER` and a bare `<a href>` would 401 for a signed-in user.
 */
function brochurePath(tourId: string): string {
  return `/tours/${encodeURIComponent(tourId)}/brochure`;
}

export async function downloadTourBrochure(tourId: string, filename: string): Promise<void> {
  const blob = await fetchAuthenticatedBlob(
    brochurePath(tourId),
    'application/pdf',
    'Could not download the brochure',
  );
  saveBlob(blob, filename);
}

/**
 * What `viewTourBrochure` actually managed to do.
 *
 * `'downloaded-instead'` is not a failure and must not be reported as one — the
 * person has the file — but it IS a surprise worth naming, because they asked
 * for a tab and got a download.
 */
export type BrochureViewOutcome = 'opened' | 'downloaded-instead';

/**
 * Open the brochure in a new tab.
 *
 * THE `window.open` HAPPENS BEFORE THE FIRST `await`, DELIBERATELY. An async
 * function's body runs synchronously up to its first suspension point, so
 * called straight from a click handler this `open` is still inside the user
 * gesture and the popup blocker allows it. Moving it below the fetch — the
 * obvious "open the tab once we have the bytes" ordering — puts it in a later
 * microtask with no gesture attached, and Chrome and Safari both silently
 * refuse it. That failure is invisible in development, where the blocker is
 * usually off.
 *
 * The tab is opened blank and navigated to the blob: URL afterwards. It cannot
 * be pointed at the API path instead: a top-level navigation sends no
 * `Authorization` header, which is the whole trap this module exists to avoid.
 *
 * `opener` is cleared rather than passing `'noopener'` to `window.open` — that
 * feature makes `open` return `null`, and the handle is needed to navigate the
 * tab at all.
 *
 * If the tab was blocked anyway (an aggressive blocker, an embedded webview),
 * the file is SAVED instead of leaving the click doing nothing, and the caller
 * is told which of the two happened.
 */
export async function viewTourBrochure(
  tourId: string,
  filename: string,
): Promise<BrochureViewOutcome> {
  const tab = window.open('', '_blank');
  if (tab !== null) {
    tab.opener = null;
  }

  let blob: Blob;
  try {
    blob = await fetchAuthenticatedBlob(
      brochurePath(tourId),
      'application/pdf',
      'Could not open the brochure',
    );
  } catch (error: unknown) {
    // Close the placeholder rather than stranding a blank tab beside the error
    // message the caller is about to render.
    tab?.close();
    throw error;
  }

  if (tab === null) {
    saveBlob(blob, filename);
    return 'downloaded-instead';
  }

  const url = URL.createObjectURL(blob);
  tab.location.href = url;
  /**
   * Revoked on a timer, NOT immediately.
   *
   * A saved file is copied out of the blob synchronously during the click, so
   * `saveBlob` can revoke at once. A tab has to LOAD the URL first, and a
   * revoke that lands before it does leaves the new tab on a blank error page.
   * The delay is generous because the cost of overshooting is a few megabytes
   * held a minute longer, while the cost of undershooting is the feature not
   * working at all on a slow machine.
   */
  window.setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 60_000);

  return 'opened';
}

/**
 * Print a document — through the SAME route the PDF renderer uses.
 *
 * `window.print()` on the app screen was the old approach and it could never
 * match Download: it photographs whatever is on screen and relies on
 * `data-print-hide` to strip the rest, so the page header and the grey document
 * board leaked onto paper while Download rendered the bare sheet.
 *
 * Here the browser loads `/print/:type/:id` in a hidden iframe — byte-identical
 * markup to what headless Chrome renders — and prints THAT. The two paths now
 * share one template, so they cannot drift.
 */
export async function printDocument(kind: PdfKind, id: string): Promise<void> {
  // Same defect, same fix as `downloadDocumentPdf` above — this one is plain
  // JSON, so it takes `apiFetch` directly. A print token is worthless without a
  // session, so an aged-out access token must refresh here too rather than
  // leaving Print permanently broken while every other screen works.
  const { token } = await apiFetch<{ token: string }>(
    `/${kind}/${encodeURIComponent(id)}/print-token`,
  ).catch((error: unknown) => {
    throw asDisplayError(error, 'Could not prepare the document');
  });

  const type = PRINT_TYPE[kind];

  const frame = document.createElement('iframe');
  // Off-screen rather than `display:none` — a hidden iframe does not lay out,
  // and an unlaid-out document prints blank.
  frame.setAttribute('aria-hidden', 'true');
  frame.style.position = 'fixed';
  frame.style.right = '0';
  frame.style.bottom = '0';
  frame.style.width = '794px';
  frame.style.height = '1123px';
  frame.style.opacity = '0';
  frame.style.pointerEvents = 'none';
  frame.style.border = '0';
  frame.src = `/print/${type}/${encodeURIComponent(id)}?token=${encodeURIComponent(token)}`;

  await new Promise<void>((resolve) => {
    let settled = false;
    const finish = (): void => {
      if (settled) return;
      settled = true;
      // Give the frame a moment after print() returns before tearing it down;
      // removing it synchronously cancels the dialog in some browsers.
      window.setTimeout(() => {
        frame.remove();
        resolve();
      }, 1000);
    };

    frame.onload = () => {
      const win = frame.contentWindow;
      if (win === null) {
        finish();
        return;
      }
      // Wait for the document to signal it has rendered — the same marker the
      // PDF service waits on — so we never print a loading skeleton.
      const started = Date.now();
      const tick = window.setInterval(() => {
        const ready =
          win.document.querySelector('[data-print-ready="true"]') !== null ||
          Date.now() - started > 10_000;
        if (!ready) return;
        window.clearInterval(tick);
        win.focus();
        win.print();
        finish();
      }, 120);
    };

    document.body.appendChild(frame);
  });
}
